import { computed, ref, shallowRef, triggerRef } from 'vue';

import usePopulationDefaultUsage from '@/composables/dataSharingHub/usePopulationDefaultUsage';
import useSharingRulePresetInfo, {
  type PresetInfo,
} from '@/composables/dataSharingHub/useSharingRulePresetInfo';
import {
  CROSSBEAM_DEFAULT_PRESET_TYPE,
  HIDDEN,
  POPULATION_RULE,
  SINGLE_SHARE_PRESET_TYPE,
  type SharingType,
} from '@/constants/data_shares';
import {
  useDataSharePresetsStore,
  useDataSharesStore,
  usePartnersStore,
  usePopulationsStore,
} from '@/stores';
import type { DataSharePreset } from '@/types/data_share_presets';
import type { OutgoingSharingRule } from '@/types/data_shares';
import type { Partner } from '@/types/partners';
import type {
  CUSTOMERS_STANDARD_TYPE,
  OPEN_OPPS_STANDARD_TYPE,
  PROSPECTS_STANDARD_TYPE,
  Population,
  PopulationStats,
} from '@/types/populations';

export type FilterValues = Record<string, (string | number)[]>;

const STANDARD_TYPE_ORDER: Record<
  | typeof CUSTOMERS_STANDARD_TYPE
  | typeof OPEN_OPPS_STANDARD_TYPE
  | typeof PROSPECTS_STANDARD_TYPE,
  number
> = {
  customers: 0,
  open_opportunities: 1,
  prospects: 2,
};

/**
 * Sorts populations: standard types first (in order), then alphabetically by name.
 * Exported so other modules (e.g. PopulationSharingModal) can reuse the same order.
 */
export function sortPopulationsByStandardType(
  a: Population,
  b: Population,
): number {
  const aOrder = a.standard_type
    ? STANDARD_TYPE_ORDER[a.standard_type]
    : undefined;
  const bOrder = b.standard_type
    ? STANDARD_TYPE_ORDER[b.standard_type]
    : undefined;

  if (aOrder !== undefined && bOrder !== undefined) return aOrder - bOrder;
  if (aOrder !== undefined) return -1;
  if (bOrder !== undefined) return 1;

  return a.name.localeCompare(b.name);
}

export type PartnerSharingData = {
  visibility?: SharingType;
  presetId?: number | null;
};

export type PopulationWithSharing = {
  id: number;
  population: Population & {
    meta?: PopulationStats;
  };
  sharingRule?: OutgoingSharingRule;
  preset?: Pick<DataSharePreset, 'id' | 'name' | 'preset_type'> | null;
  usingDefaultCount?: number;
};

export type PartnerWithSharing = Partner & {
  sharingRule?: OutgoingSharingRule;
  isCustom: boolean;
  presetInfo: PresetInfo | null;
  overridesCount?: number;
};

export type SubheaderItem = {
  type: 'subheader';
  title: string;
  id: string;
};

export type PartnerListItem = PartnerWithSharing | SubheaderItem;

/**
 * Composable for managing sharing settings at scale from the Sharing Settings Hub page.
 * Handles both population-level and partner-level sharing configuration with bulk operations.
 */
export default function useSharingSettingsHub() {
  const populationsStore = usePopulationsStore();
  const dataSharesStore = useDataSharesStore();
  const dataSharePresetsStore = useDataSharePresetsStore();
  const partnersStore = usePartnersStore();
  const {
    getDefaultSharingRule,
    getPartnerOverrides,
    getPartnersUsingDefault,
  } = usePopulationDefaultUsage();
  const { getPresetInfoForRule } = useSharingRulePresetInfo();

  const currentPopulationValues = shallowRef<Map<number, PartnerSharingData>>(
    new Map(),
  );

  const currentPartnerValues = shallowRef<Map<number, PartnerSharingData>>(
    new Map(),
  );

  // State
  const searchValue = ref('');
  const activeFilters = ref<FilterValues>({});

  // ==========================================
  // POPULATION VIEW LOGIC
  // ==========================================

  /**
   * Enriches populations with their sharing rules, presets, and partner counts
   */
  const populationsWithSharing = computed<PopulationWithSharing[]>(() => {
    const populations = populationsStore.populations;
    const sharingRules = dataSharesStore.getOutgoingSharingRules({
      ruleType: POPULATION_RULE,
    });

    return populations
      .map((population) => {
        const sharingRule = sharingRules.find(
          (rule) => rule.population_id === population.id,
        );

        const preset = getPresetInfo(sharingRule);
        const usingDefaultCount = getPartnersUsingDefaultRule(population.id);

        return {
          id: population.id,
          population,
          sharingRule,
          preset,
          usingDefaultCount,
        };
      })
      .sort((a, b) =>
        sortPopulationsByStandardType(a.population, b.population),
      );
  });

  /**
   * Filters populations based on search and active filters
   * Filters operate on CURRENT values (including unsaved changes)
   */
  const filteredPopulations = computed(() => {
    let result = populationsWithSharing.value;

    // Search filter
    if (searchValue.value) {
      const search = searchValue.value.toLowerCase();
      result = result.filter((item) =>
        item.population.name.toLowerCase().includes(search),
      );
    }

    // Population type filter
    if (activeFilters.value.population_type?.length) {
      result = result.filter(
        (item) =>
          item.population.segment &&
          activeFilters.value.population_type?.includes(
            item.population.segment,
          ),
      );
    }

    // Sharing level filter - check current value (with changes)
    if (activeFilters.value.sharing_level?.length) {
      result = result.filter((item) => {
        const changes = currentPopulationValues.value.get(item.population.id);
        const visibility =
          changes?.visibility ?? item.sharingRule?.visibility ?? HIDDEN;
        return activeFilters.value.sharing_level?.includes(visibility);
      });
    }

    // Field presets filter - check current value (with changes)
    if (activeFilters.value.field_presets?.length) {
      result = result.filter((item) => {
        const changes = currentPopulationValues.value.get(item.population.id);
        const currentPresetId = changes?.presetId ?? item.preset?.id;

        // If presetId changed, look up the new preset
        let preset = item.preset;
        if (
          changes?.presetId !== undefined &&
          changes.presetId !== item.preset?.id
        ) {
          preset = currentPresetId
            ? dataSharePresetsStore.allPresets.find(
                (p) => p.id === currentPresetId,
              ) || null
            : null;
        }

        if (!preset) return false;

        return activeFilters.value.field_presets?.some((filterValue) => {
          if (filterValue === CROSSBEAM_DEFAULT_PRESET_TYPE) {
            return preset?.preset_type === CROSSBEAM_DEFAULT_PRESET_TYPE;
          }
          return preset?.name === filterValue;
        });
      });
    }

    return result;
  });

  // ==========================================
  // PARTNER VIEW LOGIC (for a specific population)
  // ==========================================

  /**
   * Gets partners with their sharing settings for a specific population
   */
  function getPartnersForPopulation(populationId: number): {
    customPartners: PartnerWithSharing[];
    defaultPartners: PartnerWithSharing[];
    allPartners: PartnerWithSharing[];
  } {
    const population = populationsStore.getPopulationById(populationId);
    if (!population) {
      return { customPartners: [], defaultPartners: [], allPartners: [] };
    }

    const partners = partnersStore.partnerOrgs.filter(
      (partner) => !partner.offline_partner && !partner.virtual_partner,
    );
    const defaultRule = getDefaultSharingRule(populationId);
    const overridesByPartner = getPartnerOverrides(populationId);
    const getOverrideCount = dataSharesStore.outgoingOverrideCountLookup;
    const defaultPresetInfo = getPresetInfoForRule(defaultRule, population);

    const customPartners: PartnerWithSharing[] = [];
    const defaultPartners: PartnerWithSharing[] = [];

    partners.forEach((partner) => {
      const partnerOverride = overridesByPartner[partner.id];
      const sharingRule = partnerOverride ?? defaultRule;
      const presetInfo = partnerOverride
        ? getPresetInfoForRule(sharingRule, population)
        : defaultPresetInfo;

      const overridesCount = getOverrideCount(populationId, partner.id);
      const isCustom = !!partnerOverride || overridesCount > 0;

      const partnerWithSharing: PartnerWithSharing = {
        ...partner,
        sharingRule,
        isCustom,
        presetInfo,
        overridesCount,
      };

      if (isCustom) {
        customPartners.push(partnerWithSharing);
      } else {
        defaultPartners.push(partnerWithSharing);
      }
    });

    return {
      customPartners,
      defaultPartners,
      allPartners: [...customPartners, ...defaultPartners],
    };
  }

  /**
   * Combines custom and default partners into a single list with subheaders
   */
  function combinePartnersWithSubheaders(
    customPartners: PartnerWithSharing[],
    defaultPartners: PartnerWithSharing[],
  ): PartnerListItem[] {
    const list: PartnerListItem[] = [];

    // Add custom partners section if there are any
    if (customPartners.length > 0) {
      list.push({
        type: 'subheader',
        title: 'Custom Sharing Settings',
        id: 'subheader-custom',
      });
      list.push(...customPartners);
    }

    // Add default partners section
    if (defaultPartners.length > 0) {
      list.push({
        type: 'subheader',
        title: 'Default Sharing Settings',
        id: 'subheader-default',
      });
      list.push(...defaultPartners);
    }

    return list;
  }

  /**
   * Filters partners based on search and filters
   * Filters operate on CURRENT values (including unsaved changes)
   */
  function filterPartners(
    partners: PartnerWithSharing[],
    search: string,
    filters: FilterValues,
  ): PartnerWithSharing[] {
    let result = partners;

    // Search filter
    if (search) {
      const searchLower = search.toLowerCase();
      result = result.filter((p) => p.name.toLowerCase().includes(searchLower));
    }

    // Partner tag filter
    if (filters.partner_tag?.length) {
      result = result.filter((p) => {
        const partnerTagIds = p.tags?.map((tag) => tag.id) || [];
        return filters.partner_tag?.some((filterTagId) =>
          partnerTagIds.includes(filterTagId),
        );
      });
    }

    // Sharing level filter - check current value (with changes)
    if (filters.sharing_level?.length) {
      result = result.filter((p) => {
        const changes = currentPartnerValues.value.get(p.id);
        const visibility =
          changes?.visibility ?? p.sharingRule?.visibility ?? HIDDEN;
        return filters.sharing_level?.includes(visibility);
      });
    }

    // Field presets filter - check current value (with changes)
    if (filters.field_presets?.length) {
      result = result.filter((p) => {
        const presetInfo = getCurrentPartnerPresetInfo(p);
        if (!presetInfo) return false;

        return filters.field_presets?.some((filterValue) => {
          if (
            filterValue === CROSSBEAM_DEFAULT_PRESET_TYPE ||
            filterValue === SINGLE_SHARE_PRESET_TYPE
          ) {
            return presetInfo.presetType === filterValue;
          }
          return presetInfo.presetName === filterValue;
        });
      });
    }

    return result;
  }

  /** The partner's preset, including a change not saved yet. */
  function getCurrentPartnerPresetInfo(
    partner: PartnerWithSharing,
  ): PresetInfo | null {
    const changes = currentPartnerValues.value.get(partner.id);
    if (
      changes?.presetId === undefined ||
      changes.presetId === partner.presetInfo?.presetId
    )
      return partner.presetInfo;

    const currentPresetId = changes.presetId ?? partner.presetInfo?.presetId;
    const preset = currentPresetId
      ? dataSharePresetsStore.allPresets.find(
          ({ id }) => id === currentPresetId,
        )
      : undefined;
    if (!preset) return null;

    return {
      presetId: preset.id,
      presetType: preset.preset_type,
      presetName: preset.name || dataSharePresetsStore.crossbeamPresetLabel,
      fieldCount: 0,
    };
  }

  function hasCustomFieldSelection(partners: PartnerWithSharing[]) {
    return partners.some(
      (partner) =>
        getCurrentPartnerPresetInfo(partner)?.presetType ===
        SINGLE_SHARE_PRESET_TYPE,
    );
  }

  // ==========================================
  // HELPER FUNCTIONS
  // ==========================================

  /**
   * Counts partners whose effective sharing matches the population default
   * and have no pop-to-pop overrides.
   *
   * Uses VALUE MATCHING (not rule existence) to align with the
   * SharingSettingsPopulation combinedPartnersList classification that
   * determines which partners appear under "Default Sharing Settings".
   */
  function getPartnersUsingDefaultRule(populationId: number): number {
    return getPartnersUsingDefault(populationId).partnersUsingDefaults.length;
  }
  /**
   * Gets preset information from a sharing rule
   */
  function getPresetInfo(
    sharingRule?: OutgoingSharingRule,
  ): Pick<DataSharePreset, 'id' | 'name' | 'preset_type'> | null {
    if (!sharingRule?.data_share_presets_id) {
      return null;
    }

    const presetFromStore = dataSharePresetsStore.allPresets.find(
      (p) => p.id === sharingRule.data_share_presets_id,
    );

    return presetFromStore
      ? {
          id: presetFromStore.id,
          name: presetFromStore.name || '',
          preset_type: presetFromStore.preset_type,
        }
      : null;
  }

  // ==========================================
  // STATE MANAGEMENT
  // ==========================================

  function applySearch(value: string) {
    searchValue.value = value;
  }

  function applyFilters(filterValues: FilterValues) {
    activeFilters.value = filterValues;
  }

  function clearFilters() {
    searchValue.value = '';
    activeFilters.value = {};
  }

  /**
   * Update current values for a population (called when changes are made)
   */
  function updatePopulationCurrentValues(
    populationId: number,
    values: PartnerSharingData,
  ) {
    const existing = currentPopulationValues.value.get(populationId) || {};
    currentPopulationValues.value.set(populationId, {
      ...existing,
      ...values,
    });
    triggerRef(currentPopulationValues);
  }

  /**
   * Update current values for a partner (called when changes are made)
   */
  function updatePartnerCurrentValues(
    partnerId: number,
    values: PartnerSharingData,
  ) {
    const existing = currentPartnerValues.value.get(partnerId) || {};
    currentPartnerValues.value.set(partnerId, { ...existing, ...values });
    triggerRef(currentPartnerValues);
  }

  return {
    // Population view
    populationsWithSharing,
    filteredPopulations,

    // Partner view (for a specific population)
    getPartnersForPopulation,
    filterPartners,
    hasCustomFieldSelection,
    combinePartnersWithSubheaders,
    getDefaultSharingRule,
    getPartnerOverrides,

    // Search & Filters
    searchValue,
    activeFilters,
    applySearch,
    applyFilters,
    clearFilters,

    // Current values tracking (for filtering on unsaved changes)
    updatePopulationCurrentValues,
    updatePartnerCurrentValues,
    currentPartnerValues,
  };
}
